import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSessionContext } from '@supabase/auth-helpers-react';

import PasswordSecurityFeedback from './forms/PasswordSecurityFeedback';
import { obtenerMensajeErrorAuth } from '../utils/authMessages';
import { evaluarPassword, validarNuevaPassword } from '../utils/passwordSecurity';
import { supabase } from '../supabase';
import { useAvisosAutomaticos } from '../routes/Vencimientos/useAvisosAutomaticos';
import NotificacionesDropdown from './NotificacionesDropdown';

import {
  Alert,
  Button,
  Container,
  FloatingLabel,
  Form,
  Modal,
  Nav,
  Navbar,
  NavDropdown,
  Spinner,
} from 'react-bootstrap';

import {
  BoxArrowRight,
  Boxes,
  CalendarCheck,
  Gear,
  Key,
  PersonCircle,
} from 'react-bootstrap-icons';

export default function AppNavbar() {
  const { session } = useSessionContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { avisos, loading: avisosLoading } = useAvisosAutomaticos();

  const [expanded, setExpanded] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mostrarPass, setMostrarPass] = useState(false);
  const [passwordResult, setPasswordResult] = useState(null);
  const [savingPassword, setSavingPassword] = useState(false);
  const closePasswordTimerRef = useRef(null);

  useEffect(() => () => {
    if (closePasswordTimerRef.current) {
      clearTimeout(closePasswordTimerRef.current);
    }
  }, []);

  const navClassName = (path) => {
    const active =
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`);

    return `custom-navbar-link${active ? ' is-active' : ''}`;
  };

  const passwordSecurity = useMemo(
    () => evaluarPassword(newPassword),
    [newPassword]
  );

  const {
    checks: passwordChecks,
    strength: strengthData,
    isValid: passwordIsValid,
  } = passwordSecurity;

  const passwordsMatch =
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    newPassword === confirmPassword;

  const canSavePassword =
    passwordIsValid &&
    passwordsMatch &&
    !savingPassword;

  const userEmail = session?.user?.email || '';

  const closeNavbar = () => {
    setExpanded(false);
  };

  const limpiarPasswordResult = () => {
    if (passwordResult) {
      setPasswordResult(null);
    }
  };

  const handleLogout = async () => {
    closeNavbar();
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };

  const handleOpenPasswordModal = () => {
    closeNavbar();
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    if (savingPassword) return;

    if (closePasswordTimerRef.current) {
      clearTimeout(closePasswordTimerRef.current);
      closePasswordTimerRef.current = null;
    }

    setShowPasswordModal(false);
    setNewPassword('');
    setConfirmPassword('');
    setMostrarPass(false);
    setPasswordResult(null);
    setSavingPassword(false);
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();

    if (savingPassword) return;

    const validationError = validarNuevaPassword(
      newPassword,
      confirmPassword
    );

    if (validationError) {
      setPasswordResult({
        variant: 'danger',
        message: validationError,
      });
      return;
    }

    setSavingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setPasswordResult({
        variant: 'success',
        message: 'Contraseña actualizada correctamente.',
      });

      closePasswordTimerRef.current = setTimeout(() => {
        handleClosePasswordModal();
      }, 1500);
    } catch (error) {
      setPasswordResult({
        variant: 'danger',
        message: obtenerMensajeErrorAuth(
          error,
          'No fue posible actualizar la contraseña. Intentá nuevamente.'
        ),
      });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <>
      <Navbar
        bg='dark'
        variant='dark'
        expand='xl'
        className='custom-navbar'
        sticky='top'
        expanded={expanded}
        onToggle={setExpanded}
      >
        <Container fluid className='custom-navbar-container'>
          <Navbar.Brand
            as={Link}
            to='/'
            className='custom-navbar-brand'
            onClick={closeNavbar}
          >
            <span className='custom-navbar-logo'>
              <img src='/delyar_sa_logo.png' alt='Delyar' />
            </span>

            <span className='custom-navbar-title'>
              Servicios y Vencimientos
            </span>
          </Navbar.Brand>

          <Navbar.Toggle
            aria-controls='navbar'
            className='custom-navbar-toggle'
          />

          <Navbar.Collapse
            id='navbar'
            className='custom-navbar-collapse'
          >
            <Nav className='custom-navbar-links'>
              <Nav.Link
                as={Link}
                to='/servicios'
                className={navClassName('/servicios')}
                onClick={closeNavbar}
              >
                <Gear />
                <span>Servicios</span>
              </Nav.Link>

              <Nav.Link
                as={Link}
                to='/vencimientos'
                className={navClassName('/vencimientos')}
                onClick={closeNavbar}
              >
                <CalendarCheck />
                <span>Vencimientos</span>
              </Nav.Link>

              <Nav.Link
                as={Link}
                to='/partidas'
                className={navClassName('/partidas')}
                onClick={closeNavbar}
              >
                <Boxes />
                <span>Vencimiento de Partidas</span>
              </Nav.Link>
            </Nav>

            <Nav className='custom-navbar-account'>
              <NotificacionesDropdown
                avisos={avisos}
                loading={avisosLoading}
                onVerTodos={() => {
                  closeNavbar();
                  navigate('/vencimientos');
                }}
              />

              <NavDropdown
                align='end'
                id='account-dropdown'
                className='account-dropdown'
                title={
                  <span className='custom-account-title'>
                    <PersonCircle />
                    <span>Mi Cuenta</span>
                  </span>
                }
              >
                <NavDropdown.Item
                  disabled
                  className='text-muted small'
                >
                  {userEmail}
                </NavDropdown.Item>

                <NavDropdown.Divider />

                <NavDropdown.Item onClick={handleOpenPasswordModal}>
                  <Key className='me-2' />
                  Cambiar Contraseña
                </NavDropdown.Item>

                <NavDropdown.Item
                  onClick={handleLogout}
                  className='text-danger'
                >
                  <BoxArrowRight className='me-2' />
                  Cerrar Sesión
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Modal
        show={showPasswordModal}
        onHide={handleClosePasswordModal}
        centered
        className='recover-modal'
        backdrop={savingPassword ? 'static' : true}
        keyboard={!savingPassword}
      >
        <Modal.Header closeButton={!savingPassword}>
          <Modal.Title>Cambiar Contraseña</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className='recover-info mt-0'>
            Elegí una nueva contraseña segura para proteger tu cuenta.
          </div>

          <Form
            onSubmit={handleChangePassword}
            className='login-form mt-4'
          >
            <FloatingLabel
              controlId='navbarNewPassword'
              label='Nueva contraseña'
            >
              <Form.Control
                type={mostrarPass ? 'text' : 'password'}
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  limpiarPasswordResult();
                }}
                required
                placeholder='Nueva contraseña'
                autoComplete='new-password'
                disabled={savingPassword}
              />
            </FloatingLabel>

            <PasswordSecurityFeedback
              checks={passwordChecks}
              strength={strengthData}
            />

            <FloatingLabel
              controlId='navbarConfirmPassword'
              label='Confirmar contraseña'
            >
              <Form.Control
                type={mostrarPass ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  limpiarPasswordResult();
                }}
                required
                placeholder='Confirmar contraseña'
                autoComplete='new-password'
                disabled={savingPassword}
              />
            </FloatingLabel>

            {confirmPassword && (
              <div
                className={`password-match ${
                  passwordsMatch ? 'is-valid' : ''
                }`}
              >
                {passwordsMatch
                  ? 'Las contraseñas coinciden.'
                  : 'Las contraseñas no coinciden.'}
              </div>
            )}

            <Form.Check
              type='checkbox'
              label='Mostrar contraseña'
              checked={mostrarPass}
              onChange={(event) => setMostrarPass(event.target.checked)}
              disabled={savingPassword}
            />

            {passwordResult && (
              <Alert
                variant={passwordResult.variant}
                className='mt-3 mb-0'
                role='status'
                aria-live='polite'
              >
                {passwordResult.message}
              </Alert>
            )}

            <div className='d-flex flex-column flex-sm-row justify-content-end gap-2 mt-4'>
              <Button
                variant='secondary'
                onClick={handleClosePasswordModal}
                disabled={savingPassword}
              >
                Cancelar
              </Button>

              <Button
                type='submit'
                variant='primary'
                disabled={!canSavePassword}
              >
                {savingPassword ? (
                  <>
                    <Spinner
                      animation='border'
                      size='sm'
                      className='me-2'
                    />
                    Guardando...
                  </>
                ) : (
                  'Guardar'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}
